/**
 * Integration Key service — wraps the backend /api/v1/integrations API.
 *
 * Raw keys are sent ONLY via `createKey`/`updateKey` payloads. Responses
 * never include the raw key — just a masked `key_preview`.
 */
import api from './api';
import {
  IntegrationKey,
  IntegrationKeyCreate,
  IntegrationKeyUpdate,
  IntegrationTestResponse,
  ProviderMetadata,
} from '../types/integration';

export interface ServiceResult<T> {
  data?: T;
  error?: string;
}

function extractError(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.detail ||
    err?.message ||
    fallback
  );
}

export async function listIntegrationKeys(): Promise<ServiceResult<IntegrationKey[]>> {
  try {
    const { data } = await api.get<IntegrationKey[]>('/integrations');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load integration keys') };
  }
}

export async function listProviders(): Promise<ServiceResult<ProviderMetadata[]>> {
  try {
    const { data } = await api.get<ProviderMetadata[]>('/integrations/providers');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load provider metadata') };
  }
}

export async function createIntegrationKey(
  payload: IntegrationKeyCreate,
): Promise<ServiceResult<IntegrationKey>> {
  try {
    const { data } = await api.post<IntegrationKey>('/integrations', payload);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to save integration key') };
  }
}

export async function updateIntegrationKey(
  id: number,
  payload: IntegrationKeyUpdate,
): Promise<ServiceResult<IntegrationKey>> {
  try {
    const { data } = await api.patch<IntegrationKey>(`/integrations/${id}`, payload);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to update integration key') };
  }
}

export async function deleteIntegrationKey(id: number): Promise<ServiceResult<boolean>> {
  try {
    await api.delete(`/integrations/${id}`);
    return { data: true };
  } catch (err) {
    return { error: extractError(err, 'Failed to delete integration key') };
  }
}

export async function testIntegrationKey(
  id: number,
): Promise<ServiceResult<IntegrationTestResponse>> {
  try {
    const { data } = await api.post<IntegrationTestResponse>(`/integrations/${id}/test`);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Test connection failed') };
  }
}

// ---------------------------------------------------------------------------
// Balance + burn-rate (Phase 1)
// ---------------------------------------------------------------------------

export interface UsageWindow {
  provider: string;
  days: number;
  input_tokens: number;
  output_tokens: number;
  usd_total: number;
  avg_usd_per_day: number;
  runway_days: number | null;
}

export interface BalanceResponse {
  integration_id: number;
  provider: string;
  balance_usd: number | null;
  balance_updated_at: string | null;
  balance_source: 'auto' | 'manual' | null;
  auto_refresh_supported: boolean;
  auto_refresh_detail?: string | null;
  usage: UsageWindow;
}

export async function getIntegrationBalance(
  id: number, opts?: { refresh?: boolean },
): Promise<ServiceResult<BalanceResponse>> {
  try {
    const { data } = await api.get<BalanceResponse>(
      `/integrations/${id}/balance`,
      { params: opts?.refresh ? { refresh: true } : undefined },
    );
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to read balance') };
  }
}

export async function setIntegrationBalance(
  id: number, balance_usd: number | null,
): Promise<ServiceResult<BalanceResponse>> {
  try {
    const { data } = await api.patch<BalanceResponse>(
      `/integrations/${id}/balance`,
      { balance_usd },
    );
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to update balance') };
  }
}

const integrationService = {
  listIntegrationKeys,
  listProviders,
  createIntegrationKey,
  updateIntegrationKey,
  deleteIntegrationKey,
  testIntegrationKey,
  getIntegrationBalance,
  setIntegrationBalance,
};

export default integrationService;
